import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useVariantMutations } from '@/features/patient-record/hooks/useVariantMutations';
import { patientQueries } from '@/features/patient-record/queries';
import type { GeneticVariantViewModel } from '@/features/patient-record/types';

import { ClinicalSection, type ClinicalSectionLabels } from '../ClinicalSection';
import { ConfirmDeleteDialog } from '../_shared/ConfirmDeleteDialog';

import { VariantFormDialog } from './VariantFormDialog';
import { VariantRow } from './VariantRow';

interface GeneticInfoSectionProps {
  readonly guid: string;
  /**
   * Which side the per-gene term-info popover opens toward (PTV2-1298). The section is mounted in
   * both the patient record (room to the right → default `right`) and the pedigree node editor's
   * right-hand rail (must open `left` to stay on screen).
   */
  readonly termInfoSide?: 'left' | 'right';
}

/**
 * Genetic variants for a patient. Supports create, edit-all-fields, and
 * delete-with-confirm via the dialog. The dedicated gene/HGVS picker
 * (P1-04) replaces the free-text gene + variant inputs later.
 */
export function GeneticInfoSection({
  guid,
  termInfoSide = 'right',
}: GeneticInfoSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({ ...patientQueries.geneticVariants(guid), placeholderData: keepPreviousData });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<GeneticVariantViewModel | null>(null);
  const [pendingDelete, setPendingDelete] = useState<GeneticVariantViewModel | null>(null);
  const { remove } = useVariantMutations(guid);

  const labels: ClinicalSectionLabels = {
    title: t('patient.geneticInfo.heading'),
    empty: t('patient.geneticInfo.empty'),
    loading: t('patient.geneticInfo.loading'),
    errorHeading: t('patient.geneticInfo.error.heading'),
    errorMessage: t('patient.geneticInfo.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.patient.error.retry'),
  };

  return (
    <>
      <ClinicalSection
        id="genetic-info"
        labels={labels}
        query={query}
        summary={(query.data ?? []).map((v) => ({ label: v.gene, value: v.variant || '—', filled: true }))}
        itemKey={(variant) => variant.id}
        renderItem={(variant) => (
          <VariantRow
            variant={variant}
            termInfoSide={termInfoSide}
            onEdit={() => {
              setEditing(variant);
            }}
            onDelete={() => {
              setPendingDelete(variant);
            }}
          />
        )}
        headerAction={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {t('patient.geneticInfo.addVariant')}
          </Button>
        }
      />
      {creating && (
        <VariantFormDialog
          guid={guid}
          mode="create"
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {editing !== null && (
        <VariantFormDialog
          guid={guid}
          mode="edit"
          variant={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('patient.geneticInfo.confirmDeleteTitle')}
          message={t('patient.geneticInfo.confirmDeleteMessage', {
            gene: pendingDelete.gene,
            variant: pendingDelete.variant,
          })}
          confirmLabel={t('patient.geneticInfo.delete')}
          busy={remove.isPending}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            remove.mutate(pendingDelete.id, {
              onSettled: () => {
                setPendingDelete(null);
              },
            });
          }}
        />
      )}
    </>
  );
}
